import * as React from 'react';
import { Download, Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { downloadCsv } from '../../../../../lib/downloadBlob';

export function PublisherCsvManageDialog({
  kind,
  filename,
  downloadPending,
  uploadPending,
  onDownload,
  onUpload,
  onClose,
}: {
  kind: 'apps' | 'domains';
  filename: string;
  downloadPending: boolean;
  uploadPending: boolean;
  onDownload: () => Promise<string>;
  onUpload: (file: File) => Promise<unknown>;
  onClose: () => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const pending = downloadPending || uploadPending;
  const guard = useBusyDialogGuard(pending, onClose);

  async function download() {
    try {
      downloadCsv(await onDownload(), filename);
    } catch (error) {
      showRequestError(error, dashboardT(`publisherDetails.${kind}.manageDownloadError`));
    }
  }

  async function upload() {
    if (!file) return;
    try {
      await onUpload(file);
      toast.success(dashboardT(`publisherDetails.${kind}.manageSuccess`));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT(`publisherDetails.${kind}.manageError`));
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-[15px]">
            {dashboardT(`publisherDetails.${kind}.manageTitle`)}
          </DialogTitle>
          <DialogDescription>
            {dashboardT(`publisherDetails.${kind}.manageDescription`)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 px-5 py-5">
          <section className="space-y-3">
            <p className="text-sm">{dashboardT(`publisherDetails.${kind}.manageDownload`)}</p>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => void download()}
            >
              {downloadPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
              {dashboardT(`publisherDetails.${kind}.downloadAll`)}
            </Button>
          </section>
          <section className="space-y-3">
            <p className="text-sm">{dashboardT(`publisherDetails.${kind}.manageUpload`)}</p>
            <FilePicker
              label={dashboardT('publisherDetails.csvFile')}
              buttonLabel={dashboardT('publisherDetails.chooseFile')}
              accept=".csv,text/csv"
              value={file}
              onFileChange={setFile}
              required
              disabled={pending}
            />
          </section>
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button type="button" variant="outline" disabled={pending} onClick={guard.close}>
            {dashboardT('publisherDetails.cancel')}
          </Button>
          <Button type="button" disabled={!file || pending} onClick={() => void upload()}>
            {uploadPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {dashboardT('publisherDetails.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
